import {
  BarChart3,
  CalendarDays,
  Download,
  Eye,
  MousePointerClick,
  RefreshCw,
  Share2,
  Users
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { formatNumber, formatPercent, formatShortDate } from "../lib/format";
import { getMockAnalyticsSummary } from "../lib/mockAnalytics";
import type { AnalyticsSummary } from "../types";

type RangeDays = 7 | 30 | 90;

function downloadCsv(summary: AnalyticsSummary): void {
  const rows = [
    ["day", "views", "clicks", "shares"],
    ...summary.daily.map((row) => [row.day, row.views, row.clicks, row.shares])
  ];
  const csv = rows.map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `cokie-links-${summary.rangeDays}-days.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function MetricCard({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof Eye;
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <article className="metric-card">
      <div className="metric-icon">
        <Icon aria-hidden="true" size={18} />
      </div>
      <div>
        <p>{label}</p>
        <strong>{value}</strong>
        <span>{detail}</span>
      </div>
    </article>
  );
}

function BarSeries({ summary }: { summary: AnalyticsSummary }) {
  const max = Math.max(...summary.daily.map((row) => Math.max(row.views, row.clicks, row.shares)), 1);

  return (
    <div className="chart-bars" aria-label="Daily views, clicks, and shares">
      {summary.daily.map((row) => (
        <div className="chart-day" key={row.day}>
          <div className="chart-stack">
            <span className="bar views" style={{ height: `${Math.max(8, (row.views / max) * 100)}%` }} />
            <span className="bar clicks" style={{ height: `${Math.max(8, (row.clicks / max) * 100)}%` }} />
            <span className="bar shares" style={{ height: `${Math.max(8, (row.shares / max) * 100)}%` }} />
          </div>
          <small>{formatShortDate(row.day)}</small>
        </div>
      ))}
    </div>
  );
}

function RankedList({ title, rows }: { title: string; rows: Array<{ label: string; count: number }> }) {
  const max = Math.max(...rows.map((row) => row.count), 1);

  return (
    <section className="rank-panel">
      <h2>{title}</h2>
      <div className="rank-list">
        {rows.map((row) => (
          <div className="rank-row" key={row.label}>
            <span>{row.label}</span>
            <div className="rank-meter" aria-hidden="true">
              <i style={{ width: `${(row.count / max) * 100}%` }} />
            </div>
            <strong>{formatNumber(row.count)}</strong>
          </div>
        ))}
      </div>
    </section>
  );
}

function LinkTable({ summary }: { summary: AnalyticsSummary }) {
  return (
    <section className="data-panel link-table-panel">
      <div className="panel-heading">
        <h2>Links</h2>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Link</th>
              <th>Clicks</th>
              <th>Shares</th>
              <th>CTR</th>
            </tr>
          </thead>
          <tbody>
            {summary.links.map((link) => (
              <tr key={link.id}>
                <td>
                  <strong>{link.title}</strong>
                  <span>{link.url}</span>
                </td>
                <td data-label="Clicks">{formatNumber(link.clicks)}</td>
                <td data-label="Shares">{formatNumber(link.shares)}</td>
                <td data-label="CTR">{formatPercent(link.clicks, summary.totals.views)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function AdminDashboard() {
  const [rangeDays, setRangeDays] = useState<RangeDays>(30);
  const [summary, setSummary] = useState<AnalyticsSummary>(() => getMockAnalyticsSummary(30));
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    fetch(`/api/summary?days=${rangeDays}`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Analytics API returned ${response.status}`);
        return response.json() as Promise<AnalyticsSummary>;
      })
      .then(setSummary)
      .catch((reason: Error) => {
        if (controller.signal.aborted) return;
        setSummary(getMockAnalyticsSummary(rangeDays));
        setError(
          reason.message.includes("Unexpected token")
            ? "Analytics API is unavailable in Vite dev; run Pages dev for D1-backed data."
            : reason.message
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [rangeDays]);

  const topReferrers = useMemo(() => summary.referrers.slice(0, 5), [summary.referrers]);

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <p className="eyebrow">cokie.typo</p>
          <h1>Analytics</h1>
        </div>
        <div className="admin-actions">
          <label className="range-control">
            <CalendarDays aria-hidden="true" size={16} />
            <select value={rangeDays} onChange={(event) => setRangeDays(Number(event.target.value) as RangeDays)}>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
          <button className="admin-button" onClick={() => downloadCsv(summary)} type="button">
            <Download aria-hidden="true" size={16} />
            CSV
          </button>
          <button className="icon-button" onClick={() => setRangeDays((current) => current)} type="button" title="Refresh">
            <RefreshCw aria-hidden="true" size={16} className={isLoading ? "spin" : undefined} />
          </button>
        </div>
      </header>

      {error ? <p className="status-note">Showing local sample data. {error}</p> : null}

      <section className="metric-grid" aria-label="Overview">
        <MetricCard icon={Eye} label="Views" value={formatNumber(summary.totals.views)} detail={`${rangeDays} day total`} />
        <MetricCard
          icon={MousePointerClick}
          label="Clicks"
          value={formatNumber(summary.totals.clicks)}
          detail={`${formatPercent(summary.totals.clicks, summary.totals.views)} CTR`}
        />
        <MetricCard
          icon={Users}
          label="Visitors"
          value={formatNumber(summary.totals.uniqueVisitors)}
          detail={summary.source === "d1" ? "Anonymous" : "Sample"}
        />
        <MetricCard icon={Share2} label="Shares" value={formatNumber(summary.totals.shares)} detail="Share menu taps" />
      </section>

      <section className="data-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Daily</p>
            <h2>Traffic Pattern</h2>
          </div>
          <div className="legend">
            <span className="legend-views">Views</span>
            <span className="legend-clicks">Clicks</span>
            <span className="legend-shares">Shares</span>
          </div>
        </div>
        <BarSeries summary={summary} />
      </section>

      <div className="admin-grid">
        <LinkTable summary={summary} />
        <div className="rank-stack">
          <RankedList title="Referrers" rows={topReferrers} />
          <RankedList title="Countries" rows={summary.countries.slice(0, 5)} />
          <RankedList title="Devices" rows={summary.devices.slice(0, 5)} />
        </div>
      </div>

      <section className="data-panel recent-panel">
        <div className="panel-heading">
          <h2>Recent Events</h2>
          <BarChart3 aria-hidden="true" size={18} />
        </div>
        {summary.recentEvents.length ? (
          <div className="recent-list">
            {summary.recentEvents.map((event) => (
              <div className="recent-row" key={`${event.createdAt}-${event.eventType}-${event.linkTitle ?? "profile"}`}>
                <time>{new Date(event.createdAt).toLocaleString()}</time>
                <strong>{event.eventType.replace("_", " ")}</strong>
                <span>{event.linkTitle ?? "Profile"}</span>
                <span>{event.referrerHost ?? "direct"}</span>
                <span>{event.country ?? "unknown"}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-state">No recent D1 events in this range.</p>
        )}
      </section>
    </main>
  );
}
